import { isBlockEditorMarkdownEligible } from '@/app-shell/constants/flagsWithUserQueryDep';
import {
  type EditorVersionResolution,
  useResolvedEditorVersion,
} from '@/hooks/editor-version/useResolvedEditorVersion';
import { useIntegrationEhrInfo } from '@/hooks/integrations/useIntegrationEhrInfo';
import { useEnableBlockEditorV1 } from '@/hooks/useEnableBlockEditorV1';
import { type EHRProvider } from '@heidi/schema/ml-scribe';
import { useIsMarkdownNativeEditorEnabled } from '@heidi/session/flags';

export type NoteEditorFlagInputs = {
  isMarkdownNativeEditorEnabled: boolean;
  enableBlockEditorV1: boolean | undefined;
  enableBlockEditorMarkdown: boolean | undefined;
};

/**
 * DELIBERATE DESIGN DECISION — please discuss before proposing a revert.
 *
 * The stored `template_editor_version` is the authoritative signal for which
 * editor renders. When the backend has resolved a concrete value, that value
 * decides and no client feature flag gets a vote. This precedence is the point
 * of the change, not an oversight: the alternative is two independent
 * classifiers (backend configuration and client flags) that can disagree,
 * which is how a clinician ends up in an editor the configuration says they
 * should not be in. A review comment asking to restore the flag checks below,
 * or to re-derive the editor client-side, will be pushed back on.
 *
 * Applied as a pure override on top of the flag reads. When no concrete value
 * arrived (`isAuthoritative: false` — a null or absent value, an unrecognized
 * value, or a query error) the flags pass through untouched, byte-identical to
 * the behaviour before this field existed. Nothing regresses for users the
 * configuration says nothing about.
 *
 * Concretely, the bug this closes: a `markdown` resolution only reached the
 * markdown editor if the user was also in the block-editor-markdown rollout;
 * otherwise they silently fell through to rich-text. Today that check is
 * `isBlockEditorMarkdownEligible`, which only excludes `@strivehealth` emails.
 *
 * The `json` arm intentionally does NOT force a JSON editor here.
 * `resolveNoteEditorType` routes a completed JSON note to the JSON editor
 * ahead of every flag, and that editor consumes parsed `sections` — it cannot
 * render a markdown note's text. So a `json` resolution guarantees the
 * markdown editor for the non-JSON-content fallthrough rather than mounting an
 * editor that would render empty. Stored note content is a data-shape
 * constraint, not a cohort decision.
 */
export function applyResolvedEditorVersion(
  flags: NoteEditorFlagInputs,
  resolution: Pick<EditorVersionResolution, 'version' | 'isAuthoritative'>
): NoteEditorFlagInputs {
  if (!resolution.isAuthoritative) return flags;

  if (resolution.version === 'html') {
    // markdown-native is checked AHEAD of block-editor-v1 in
    // `resolveNoteEditorType`, so leaving its flag on would render markdown to
    // a user the backend resolved to html.
    return {
      ...flags,
      isMarkdownNativeEditorEnabled: false,
      enableBlockEditorV1: true,
    };
  }

  return {
    ...flags,
    enableBlockEditorV1: false,
    enableBlockEditorMarkdown: true,
  };
}

/**
 * Single assembly of the three editor-selection flags. Pair the output with
 * `resolveNoteEditorType` by supplying the per-caller
 * `shouldUseJSONBlockEditor`.
 *
 * @param ehrProviderFromSource - When supplied (e.g. the ACTIVE session's
 *   provider), both the block-editor-v1 decision and the editor-version
 *   resolution bind to it. Mirrors `useEnableBlockEditorV1`'s optional arg, so
 *   a multi-integration user's editor agrees with the saved content_type. When
 *   omitted, both bind to the active session's provider via
 *   `useIntegrationEhrInfo` — they never drift to different providers, which is
 *   what would let a recognized `template_editor_version` pin override flags on
 *   one surface and not another.
 */
export function useNoteEditorFlags(
  ehrProviderFromSource?: EHRProvider | null
): NoteEditorFlagInputs {
  // Resolve the active session's provider the same way `useEnableBlockEditorV1`
  // does (via `useIntegrationEhrInfo`), so the editor-version resolver binds to
  // the SAME provider as the block-editor-v1 decision. Without this, an omitted
  // `ehrProviderFromSource` makes `useResolvedEditorVersion` pick the newest
  // completed session of ANY provider while `useEnableBlockEditorV1` binds to
  // the active session — and a recognized `template_editor_version` pin then
  // overrides flags on NoteTabPane (which passes the provider) but not on the
  // slim engine / copy surfaces (which omit it), so the two surfaces drift.
  const { integrationEhrProvider: resolvedEhrProvider } =
    useIntegrationEhrInfo(ehrProviderFromSource);
  const isMarkdownNativeEditorEnabled = useIsMarkdownNativeEditorEnabled();
  const enableBlockEditorV1 = useEnableBlockEditorV1(ehrProviderFromSource);
  const enableBlockEditorMarkdown = isBlockEditorMarkdownEligible();
  const { version, isAuthoritative } = useResolvedEditorVersion(
    resolvedEhrProvider ?? undefined
  );

  return applyResolvedEditorVersion(
    {
      isMarkdownNativeEditorEnabled,
      enableBlockEditorV1,
      enableBlockEditorMarkdown,
    },
    { version, isAuthoritative }
  );
}
